import { useState } from 'react';
import { 
  ChevronDown, 
  ChevronRight, 
  FileText, 
  Shield, 
  AlertTriangle,
  Search,
  Printer,
  Tent,
  Store
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

// Terms & Conditions Categories
const termsCategories = [
  {
    id: 1,
    title: "Event and Festival",
    icon: "tent",
    description: "Terms for festivals, multi-day events, camping deployments, temporary sites, and venue operations.",
    terms: [
      {
        id: 1,
        title: "Deployment & Arrival",
        content: [
          "Staff must arrive minimum 30 minutes before shift start unless instructed otherwise.",
          "This time is for accreditation, briefing, uniform inspection, equipment checks, and zone allocation.",
          "Failure to arrive on time must be communicated immediately to S.A.N. Administration before the client is contacted.",
          "Late arrival does not extend shift finish time unless authorised in writing by management.",
          "Unauthorised overtime is prohibited.",
          "Staff may be required to attend site one day prior for accreditation or briefing."
        ]
      },
      {
        id: 2,
        title: "Attendance & Shift Commitment",
        content: [
          "Accepted shifts are mandatory.",
          "Cancellation must follow company policy.",
          "Failure to attend may result in removal from future deployments.",
          "Leaving post without authorisation is a serious disciplinary breach.",
          "Staff must remain on duty until officially relieved."
        ]
      },
      {
        id: 3,
        title: "Professional Conduct & Behaviour",
        content: [
          "Professional conduct must be maintained:",
          "• On duty",
          "• In accommodation",
          "• In camping areas",
          "• During travel related to deployment",
          "",
          "Zero tolerance applies to:",
          "• Racism",
          "• Discrimination",
          "• Harassment",
          "• Violence",
          "• Aggressive or abusive behaviour",
          "• Intoxication",
          "• Illegal substance use",
          "",
          "Direct contact with clients is prohibited unless authorised.",
          "",
          "Staff must:",
          "• Act respectfully and professionally",
          "• Follow instructions from supervisors and authorised S.A.N. management",
          "• Represent the company with integrity at all times"
        ]
      },
      {
        id: 4,
        title: "Uniform & Appearance",
        content: [
          "Full, clean, and presentable uniform must be worn at all times.",
          "SIA badge must be clearly displayed where applicable.",
          "PPE must be worn correctly.",
          "Lost or damaged uniform or equipment may be deducted from wages.",
          "Failure to meet uniform standards may result in removal from duty without pay."
        ]
      },
      {
        id: 5,
        title: "Mobile Phone & Recording Policy",
        content: [
          "Mobile phone use is strictly prohibited unless authorised for operational reasons.",
          "Headphones or earbuds are not permitted.",
          "Taking photos, videos, or recordings while on duty or in uniform is strictly forbidden.",
          "Misuse may result in immediate removal."
        ]
      },
      {
        id: 6,
        title: "Post Conduct & Operational Rules",
        content: [
          "The following are strictly prohibited:",
          "• Sitting unless authorised",
          "• Sleeping on duty",
          "• Eating on post",
          "• Leaving assigned area without permission",
          "• Smoking or vaping in work areas",
          "• Unauthorised searches",
          "",
          "Staff must:",
          "• Patrol assigned zones regularly",
          "• Keep exits and emergency routes clear",
          "• Report hazards immediately",
          "• Record incidents accurately",
          "• Follow all site-specific instructions"
        ]
      },
      {
        id: 7,
        title: "Breaks",
        content: [
          "Breaks are authorised by supervisors only.",
          "Leaving post without coverage is a disciplinary offence.",
          "Late return from break may result in warnings or wage deductions."
        ]
      },
      {
        id: 8,
        title: "Equipment & Technology",
        content: [
          "All company equipment must be used correctly.",
          "Damage due to misuse may be deducted from wages.",
          "Radios are for operational communication only.",
          "Personal sign-in codes must not be shared."
        ]
      },
      {
        id: 9,
        title: "Reporting & Communication",
        content: [
          "Staff must:",
          "• Clock in/out using approved system",
          "• Submit daily shift reports",
          "• Report incidents immediately",
          "• Notify Admin of delays or issues",
          "• Follow all communication protocols",
          "",
          "Official communication is through designated company channels only.",
          "Management contact details must not be shared without permission."
        ]
      },
      {
        id: 10,
        title: "Accommodation & Camping Rules",
        content: [
          "Where camping or shared accommodation is provided:",
          "• Behaviour must remain professional.",
          "• Silence and respect must be maintained.",
          "• Shared areas must be kept clean and tidy.",
          "• Staff must bring their own bedding and camping essentials.",
          "• The company does not provide electricity for tents.",
          "• English must be spoken in shared accommodation where other nationalities are present to ensure inclusion and operational clarity.",
          "• Staff must remain in assigned accommodation areas only.",
          "• Alcohol and illegal substances are strictly prohibited in accommodation areas.",
          "• Camping areas are considered extensions of the workplace."
        ]
      },
      {
        id: 11,
        title: "Meals & Welfare",
        content: [
          "If catering is provided:",
          "• Meals must be collected within allocated time before deployment.",
          "• Failure to collect meals is the staff member's responsibility.",
          "",
          "Staff must remain fit for duty at all times.",
          "Staff must otherwise provide their own food, water, and weather protection."
        ]
      },
      {
        id: 12,
        title: "Payment Terms",
        content: [
          "Wages are paid monthly, one month in arrears.",
          "Pay date is the 30th of each month.",
          "Work completed in January is paid at the end of February.",
          "Pay rates are confidential.",
          "Deductions may apply for damaged uniform or equipment.",
          "Advancement opportunities depend on performance, reliability, and professionalism."
        ]
      },
      {
        id: 13,
        title: "Zero Hours Engagement",
        content: [
          "No guaranteed minimum hours.",
          "Work offered based on operational demand.",
          "Accepted shifts must be attended.",
          "Holiday pay accrues in line with UK law.",
          "Staff must comply with all site and company policies."
        ]
      },
      {
        id: 14,
        title: "Disciplinary Framework",
        content: [
          "Immediate dismissal may occur for:",
          "• Racism or discrimination",
          "• Aggression or violence",
          "• Breach of confidentiality",
          "• Intoxication",
          "• Leaving post",
          "• Repeated phone misuse",
          "• Unauthorised filming",
          "• Serious insubordination",
          "",
          "Minor breaches may result in:",
          "• Verbal warning",
          "• Written warning",
          "• Removal from future shifts",
          "• Financial deductions for damage",
          "",
          "The company reserves the right to escalate disciplinary action depending on severity."
        ]
      },
      {
        id: 15,
        title: "Acceptance",
        content: [
          "By accepting a festival or event deployment, staff confirm:",
          "• They understand these terms",
          "• They agree to comply fully",
          "• They accept operational authority of S.A.N. Security Ltd and site management"
        ]
      }
    ]
  },
  {
    id: 2,
    title: "Retail, Market, Shops",
    icon: "store",
    description: "Terms for retail stores, shopping centres, supermarkets, high street shops, and commercial premises.",
    terms: [
      {
        id: 1,
        title: "Professional Conduct",
        content: [
          "All staff must:",
          "• Maintain a professional, calm, and confident presence at all times",
          "• Treat customers, staff, and management respectfully",
          "• Remain polite even during confrontation",
          "• Avoid arguments or unnecessary escalation",
          "• Follow lawful and reasonable instructions from site management and S.A.N. supervisors",
          "",
          "Aggressive behaviour, discrimination, harassment, or abusive language will result in immediate removal."
        ]
      },
      {
        id: 2,
        title: "Punctuality & Deployment",
        content: [
          "Staff must arrive 30 minutes before shift start unless instructed otherwise.",
          "Sign in according to company procedure (App / site log / client system).",
          "Any delay must be communicated immediately to S.A.N. Administration via phone or WhatsApp.",
          "Late arrival does not authorise late finish without written approval.",
          "",
          "Failure to communicate delays is a disciplinary matter."
        ]
      },
      {
        id: 3,
        title: "Clocking, Verification & Time Recording",
        content: [
          "All staff must strictly follow company time-recording procedures:",
          "",
          "• Clock in and clock out using the S.A.N. Security Web App as instructed.",
          "• Upon arrival on site, send a clear arrival photo via WhatsApp to S.A.N. Security Administration.",
          "• Where required, clock in and clock out using the client's iPad or time system, using your unique code provided by the client or S.A.N. Administration.",
          "• All required steps (App, Photo, Client Device) must be completed where applicable.",
          "",
          "Failure to follow time recording procedures may result in adjustment of recorded hours to verified time only and may lead to disciplinary action."
        ]
      },
      {
        id: 4,
        title: "Uniform & Presentation",
        content: [
          "Full clean uniform must be worn at all times.",
          "Body-worn cameras (if issued) must be worn correctly.",
          "ID badge must be visible.",
          "No hoodies, non-uniform jackets, headphones, or hands in pockets.",
          "Personal hygiene must meet professional standards.",
          "",
          "Failure to meet uniform standards may result in being sent home without pay."
        ]
      },
      {
        id: 5,
        title: "Mobile Phone Policy",
        content: [
          "No personal mobile phone use on duty.",
          "Phones may only be used for operational purposes.",
          "Phones must not be used in front of customers.",
          "No headphones or earbuds.",
          "",
          "Unauthorised phone use may result in disciplinary action."
        ]
      },
      {
        id: 6,
        title: "Duties & Responsibilities (Retail Specific)",
        content: [
          "Staff may be required to:",
          "• Monitor entrances and exits",
          "• Prevent theft and shoplifting",
          "• Conduct lawful searches where authorised",
          "• Monitor CCTV (if trained and authorised)",
          "• Complete incident and refusal logs",
          "• Assist with customer service presence",
          "• Monitor fire exits and safety compliance",
          "• Report suspicious activity immediately",
          "",
          "Staff must remain alert and visible at all times."
        ]
      },
      {
        id: 7,
        title: "Use of Force & Conflict Management",
        content: [
          "De-escalation must always be attempted first.",
          "Any use of force must be lawful, necessary, proportionate, and reasonable.",
          "Only trained and authorised techniques may be used.",
          "All incidents involving confrontation must be fully documented.",
          "",
          "Failure to report incidents is a serious breach."
        ]
      },
      {
        id: 8,
        title: "Incident Reporting",
        content: [
          "All incidents must be recorded before shift end.",
          "Daily shift reports must be completed via company procedure.",
          "Bodycam footage (if used) must be declared.",
          "Late or missing reports may result in hour adjustments.",
          "",
          "Reports must include:",
          "• Time",
          "• Location",
          "• Names (if known)",
          "• Actions taken",
          "• Outcome"
        ]
      },
      {
        id: 9,
        title: "Communication & Client Relations",
        content: [
          "Staff must not discuss company pay, contracts, or internal matters with clients.",
          "Direct negotiation with clients is prohibited.",
          "Any client complaints must be reported immediately to management.",
          "All communication must follow company structure."
        ]
      },
      {
        id: 10,
        title: "Breaks",
        content: [
          "Breaks must be authorised by site supervisor.",
          "Staff must not leave post without cover.",
          "Returning late from break is a disciplinary matter."
        ]
      },
      {
        id: 11,
        title: "Confidentiality",
        content: [
          "Staff must not:",
          "• Share client information",
          "• Take photos or videos on site",
          "• Share incidents on social media",
          "• Discuss store losses or internal matters publicly",
          "",
          "Confidentiality breaches may result in dismissal."
        ]
      },
      {
        id: 12,
        title: "Theft & Integrity",
        content: [
          "Any suspicion of internal theft must be reported immediately.",
          "Staff must not accept gifts, cash, or benefits from customers.",
          "Staff must not misuse store discounts or privileges.",
          "",
          "Zero tolerance applies."
        ]
      },
      {
        id: 13,
        title: "Zero Hours Conditions (Retail Deployment)",
        content: [
          "Shifts are offered based on operational demand.",
          "Accepted shifts must be attended.",
          "Repeated cancellations may affect future bookings.",
          "Continued work depends on reliability and performance."
        ]
      },
      {
        id: 14,
        title: "Disciplinary Framework",
        content: [
          "Immediate removal may include:",
          "• Sleeping on duty",
          "• Leaving post unattended",
          "• Intoxication",
          "• Aggressive behaviour",
          "• Repeated lateness",
          "• Failure to submit reports",
          "• Breach of confidentiality",
          "",
          "Minor breaches may result in warnings."
        ]
      },
      {
        id: 15,
        title: "Acknowledgement",
        content: [
          "By accepting and attending a retail deployment, you confirm that you:",
          "• Have read and understood these terms",
          "• Agree to comply fully",
          "• Understand disciplinary consequences for non-compliance"
        ]
      }
    ]
  }
];

// Company Policies Data - With detailed explanations
const policiesData = [
  {
    id: 1,
    title: "Data Protection and GDPR Policy",
    sections: [
      {
        heading: "Purpose",
        content: [
          "S.A.N. Security Ltd is committed to handling personal data lawfully, fairly, and securely. This policy outlines how personal data is collected, stored, used, and protected in line with UK data protection legislation."
        ]
      },
      {
        heading: "Scope",
        content: [
          "This policy applies to personal data relating to employees, contractors, job applicants, clients, and members of the public."
        ]
      },
      {
        heading: "Data Collected",
        content: [
          "Personal data may include names, contact details, identification documents, Right to Work and licensing information, employment details, and incident-related information."
        ]
      },
      {
        heading: "Lawful Use of Data",
        content: [
          "Personal data is processed for legitimate business purposes including recruitment, workforce management, service delivery, and legal compliance."
        ]
      },
      {
        heading: "Data Storage & Security",
        content: [
          "Personal data is stored securely with access restricted to authorised personnel only."
        ]
      },
      {
        heading: "Data Retention",
        content: [
          "Personal data is retained only for as long as necessary and securely disposed of when no longer required."
        ]
      },
      {
        heading: "Individual Rights",
        content: [
          "Individuals may request access to or correction of their personal data where appropriate."
        ]
      },
      {
        heading: "Policy Review",
        content: [
          "This policy is reviewed periodically."
        ]
      },
      {
        heading: "DATA PROTECTION POLICIES - Statement and Purpose",
        content: [
          "1. S.A.N. Security LTD (the Employer, we, our or us) is committed to ensuring that all personal data handled by us will be processed according to legally compliant standards of data protection and data security.",
          "",
          "2. We confirm for the purposes of the data protection laws that the Employer is a Data Controller of the personal data we hold in connection with your employment. This means that we determine the purposes for which, and the manner in which, your personal data is processed.",
          "",
          "3. The purpose of this policy is to help us achieve our data protection and data security aims by:",
          "   a. notifying our staff of the types of personal information that we may hold about them, our customers, suppliers, and other third parties and what we do with that information;",
          "   b. setting out the rules on data protection and the legal conditions that must be satisfied when we collect, receive, handle, process, transfer and store personal data and ensuring staff understand our rules and the legal standards; and",
          "   c. clarifying the responsibilities and duties of staff in respect of data protection and data security.",
          "",
          "4. This is a statement of policy only and does not form part of your contract of employment. We may amend this policy at any time, at our absolute discretion.",
          "",
          "5. For the purposes of this policy:",
          "   a. Data Protection Laws mean all applicable laws relating to the processing of Personal Data, including, for the period during which it is in force, the UK General Data Protection Regulation and the Data Protection Act 2018.",
          "   b. Data Subject means the individual to whom personal data relates.",
          "   c. Personal Data means any information that relates to an individual who can be identified from that information.",
          "   d. Processing means any use that is made of data, including collecting, storing, amending, disclosing, or destroying it.",
          "   e. Special Categories of Personal Data means information about an individual's racial or ethnic origin, political opinions, religious or philosophical beliefs, trade union membership (or non-membership), health, sex life or sexual orientation, and biometric data."
        ]
      },
      {
        heading: "Data Protection Principles",
        content: [
          "6. Staff whose work involves using personal data relating to staff or others (e.g. customers) must comply with this policy and with the following data protection principles which require that personal information is:",
          "",
          "   a. processed lawfully, fairly, and in a transparent manner. We must always have a lawful basis to process personal data, as set out in Data Protection Laws. Personal Data may be processed as necessary to perform a contract with the Data Subject, to comply with a legal obligation which the Data Controller is the subject of, or for the legitimate interest of the Data Controller or the party to whom the data is disclosed. The Data Subject must be told who controls the information (us), the purpose(s) for which we are processing the information, and to whom it may be disclosed;",
          "",
          "   b. collected only for specified, explicit, and legitimate purposes. Personal Data must not be collected for one purpose and then used for another. If we want to change the way we use Personal Data, we must first tell the Data Subject;",
          "",
          "   c. processed only where it is adequate, relevant, and limited to what is necessary for the purposes of processing. We will only collect Personal Data to the extent required for the specific purpose notified to the Personal Data;",
          "",
          "   d. accurate and the Employer takes all reasonable steps to ensure that information that is inaccurate is rectified or deleted without delay. Checks to personal data will be made when it is collected and regular checks must be made afterwards. We will make reasonable efforts to rectify or erase inaccurate information;",
          "",
          "   e. kept only for the period necessary for processing. Information will not be kept longer than it is needed and we will take all reasonable steps to delete information when we no longer need it. For guidance on how long particular information should be kept, contact Director; and",
          "",
          "   f. secure, and appropriate measures are adopted by the Employer to ensure that it is such."
        ]
      },
      {
        heading: "Who is Responsible For Data Protection and Data Security?",
        content: [
          "7. Maintaining appropriate standards of data protection and data security is a collective task shared between us and you. This policy and the rules contained in it apply to all staff of the Employer, irrespective of seniority, tenure and working hours, including all employees, directors and officers, consultants and contractors, casual or agency staff, trainees, homeworkers and fixed-term staff and any volunteers (Staff).",
          "",
          "8. Questions about this policy, or requests for further information, should be directed to company management.",
          "",
          "9. All staff have personal responsibility to ensure compliance with this policy, to handle all Personal Data consistently with the principles set out here, and to ensure that measures are taken to protect the security of data. Managers have special responsibility for leading by example and monitoring and enforcing compliance. You must notify company management if this policy has not been followed, or if it is suspected this policy has not been followed, as soon as reasonably practicable.",
          "",
          "10. Any breach of this policy will be taken seriously and may result in disciplinary action up to and including dismissal. Significant or deliberate breaches, such as accessing staff or customer personal data without authorisation or a legitimate reason to do so, may constitute gross misconduct and could lead to dismissal without notice."
        ]
      },
      {
        heading: "What Personal Data and Activities Are Covered by This Policy?",
        content: [
          "11. This policy covers Personal Data:",
          "   a. which relates to a natural living individual who can be identified either from that information in isolation or by reading it together with other information we possess;",
          "   b. which is stored electronically or on paper in a filing system;",
          "   c. which is in the form of statements of opinion as well as facts;",
          "   d. which relates to staff (present, past or future) or to any other individual whose personal data we handle or control; and",
          "   e. which we obtain or which is provided to us, which we hold or store, organise, disclose or transfer, amend, retrieve, use, handle, process, transport or destroy.",
          "",
          "12. This Personal Data is subject to the legal safeguards set out in the Data Protection Laws."
        ]
      },
      {
        heading: "What Personal Data Do We Process About Staff?",
        content: [
          "13. We may collect Personal Data about you which:",
          "   a. You provide or we gather before or during your employment or engagement with us.",
          "   b. Is provided by third parties, such as references or information from suppliers or another party that we do business with.",
          "   c. Which is in the public domain.",
          "",
          "14. The types of Personal Data that we may collect, store and use about you include records relating to your:",
          "   a. Home address, contact details, and contact details for your next of kin.",
          "   b. Recruitment (including your application form or curriculum vitae, references received and details of your qualifications).",
          "   c. Pay records, national insurance number, and details of taxes and any employment benefits such as pensions and health insurance (including details of any claims made).",
          "   d. Telephone, email, internet, fax, or instant messenger use.",
          "   e. Performance and any disciplinary matters, grievances, complaints or concerns in which you are involved."
        ]
      },
      {
        heading: "Sensitive Personal Data",
        content: [
          "15. We may from time to time need to process sensitive personal information (i.e. 'Special Categories of Personal Data').",
          "",
          "16. We will only process sensitive personal information if:",
          "   a. we have a lawful basis for doing so (e.g. it is necessary for the performance of the employment contract); and",
          "   b. one of the following special conditions for Processing personal information applies:",
          "      I. the Data Subject has given explicit consent;",
          "      II. the Processing is necessary for the purposes of exercising the employment law rights or obligations of the Employer or the Data Subject;",
          "      III. the Processing is necessary to protect the Data Subject's vital interests, and the Data Subject is physically incapable of giving consent;",
          "      IV. the Processing relates to Personal Data which are manifestly made public by the Data Subject;",
          "      V. the Processing is necessary for the establishment, exercise, or defence of legal claims; or",
          "      VI. the Processing is necessary for reasons of substantial public interest.",
          "",
          "17. Before processing any sensitive personal information, staff must notify Director of the proposed Processing, in order for the Director to assess whether the Processing complies with the criteria noted above.",
          "",
          "18. Sensitive personal information will not be processed until the assessment above has taken place and the individual has been properly informed of the nature of the processing, the purposes for which it is being carried out, and the legal basis for it.",
          "",
          "19. Our Employee Privacy Notice (contained within this Staff Handbook) sets out the types of sensitive personal information that we process, what it is used for, and the lawful basis for the processing."
        ]
      },
      {
        heading: "How We Use Your Personal Data",
        content: [
          "20. We will tell you the reasons for Processing your Personal Data, how we use such information, and the legal basis for Processing in the Employer's Employee Privacy Notice. We will not process staff personal information for any other reason.",
          "",
          "21. In general, we will use information to carry out our business, to administer your employment or engagement, and to deal with any problems or concerns you may have. This use of information includes, but not limited to:",
          "   a. Staff address lists: to compile and circulate lists of home address and contact details, to enable us to contact you outside working hours.",
          "   b. Sickness records: we will maintain a record of your sickness absence and copies of any doctors' notes or other documents supplied to us in connection with your health, in order to:",
          "      I. inform your colleagues and others that you are absent through sickness, as reasonably necessary to manage your absence;",
          "      II. deal with unacceptably high or suspicious sickness absence;",
          "      III. inform reviewers for appraisal purposes of your sickness absence level; or",
          "      IV. publish internally aggregated, anonymous details of sickness absence levels.",
          "   c. Monitoring IT systems: to monitor your use of emails, internet, telephone and fax, computer or other communications or IT resources.",
          "   d. Disciplinary, grievance or legal matters: in connection with any disciplinary, grievance, legal, regulatory, or compliance matters or proceedings that may involve you.",
          "   e. Performance Reviews: to carry out performance reviews."
        ]
      },
      {
        heading: "Accuracy and Relevance",
        content: [
          "22. We will:",
          "   a. ensure that any Personal Data processed is up to date, accurate, adequate, relevant, and not excessive, given the purpose for which it was collected; and",
          "   b. not Process personal data obtained for one purpose for any other purpose, unless you agree to this or reasonably expect this.",
          "",
          "23. If you consider that any information held about you is inaccurate or out of date, then you should tell Director. If they agree that the information is inaccurate or out of date, then they will correct it promptly. If they do not agree with the correction, then they will note your comments."
        ]
      },
      {
        heading: "Storage and Retention",
        content: [
          "24. Personal Data (and sensitive personal information) will be kept securely in accordance with the Employer's data retention practices and procedures.",
          "",
          "25. The periods for which we hold Personal Data are contained in the Employer's Employee Privacy Notice."
        ]
      },
      {
        heading: "Individual Rights",
        content: [
          "26. You have the following rights in relation to your Personal Data:",
          "",
          "27. Subject access requests:",
          "   a. You have the right to make a subject access request. If you make a subject access request, we will tell you:",
          "      i. whether or not your Personal Data is processed and if so why, the categories of personal data concerned, and the source of the data if it is not collected from you;",
          "      ii. to whom your Personal Data is or may be disclosed;",
          "      iii. for how long your Personal Data is stored (or how that period is to be decided);",
          "      iv. your rights of rectification or erasure of data, or to restrict or object to Processing;",
          "      v. your right to complain to the Information Commissioner's Office (ICO) (at www.ico.org.uk/make-a-complaint/) if you think we have failed to comply with your data protection rights; and",
          "      vi. whether or not we carry out automated decision-making and the logic involved in any such decision making.",
          "   b. We will provide you with a copy of the Personal Data undergoing Processing. This will normally be in electronic form if you have made a request electronically, unless you agree otherwise.",
          "   c. To make a subject access request, email contact@serviceallnations.org.uk.",
          "   d. We may need to ask for proof of identification before your request can be processed. We will let you know if we need to verify your identity and the documents we require.",
          "   e. We will normally respond to your request within 28 days from the date your request is received. In some cases, where there is a large amount of Personal Data being processed, we may respond within 3 months of the date your request is received. We will write to you within 28 days of receiving your original request if this is the case.",
          "   f. If your request is manifestly unfounded or excessive we are not obliged to comply with it.",
          "",
          "28. Other rights:",
          "   a. You have a number of other rights in relation to your Personal Data. You can require us to:",
          "      i. Rectify inaccurate data.",
          "      ii. Stop Processing or erase data that is no longer necessary for the purposes of Processing.",
          "      iii. Stop Processing or erase data if your interests override our legitimate grounds for Processing the data (where we rely on our legitimate interests as a reason for Processing data).",
          "      iv. Stop Processing data for a period if data is inaccurate or if there is a dispute about whether or not your interests override the Employer's legitimate grounds for Processing the data.",
          "   b. To request that we take any of these steps, please send the request to contact@serviceallnations.org.uk."
        ]
      },
      {
        heading: "Data Security",
        content: [
          "29. We will use appropriate technical and organisational measures to keep Personal Data secure and, in particular, to protect against unauthorised or unlawful Processing and against accidental loss, destruction or damage.",
          "",
          "30. Maintaining data security means making sure that:",
          "   a. only people who are authorised to use the information can access it;",
          "   b. Personal Data is, where possible, pseudonymised or encrypted;",
          "   c. information is accurate and suitable for the purpose for which it is processed; and",
          "   d. authorised persons can access information if they need it for authorised purposes.",
          "",
          "31. By law, we must use procedures and technology to secure personal information throughout the period that we hold or control it (i.e. from obtaining to destroying the information).",
          "",
          "32. Personal information must not be transferred to any person to Process (e.g. while performing services for us on or our behalf), unless that person has either agreed to comply with our data security procedures or we are satisfied that other adequate measures exist.",
          "",
          "33. Security procedures include:",
          "   a. Any desk or cupboard containing confidential information must be kept locked.",
          "   b. Computers should be locked with a strong password that is changed regularly or should be shut down when they are left unattended and discretion should be used when viewing personal information on a monitor to ensure that it is not visible to others.",
          "   c. Data stored on CDs, memory sticks, or similar must be encrypted or password protected and the media must be locked away securely when they are not being used.",
          "   d. Use of any cloud service to store data must be approved first by Director.",
          "   e. Data should never be saved directly to mobile devices such as laptops, tablets or smartphones.",
          "   f. All servers containing Sensitive Personal Data must be approved and protected by security software.",
          "   g. Servers containing Personal Data must be kept in a secure location, away from general office space.",
          "   h. Data should be regularly backed up in line with the Employer's back-up procedure.",
          "",
          "34. Staff must abide by telephone precautions. Particular care must be taken by staff who deal with telephone enquiries to avoid inappropriate disclosures. In particular:",
          "   a. The identity of any telephone caller must be verified before any personal information is disclosed.",
          "   b. If the caller's identity cannot be verified satisfactorily then they should be asked to put their query in writing.",
          "   c. Callers must not be allowed to bully you into disclosing information. In case of any problems or uncertainty, contact Director.",
          "",
          "35. Correct methods of disposal must be adhered to.",
          "   a. Copies of personal information, whether on paper or on any physical storage device, must be physically destroyed when they are no longer needed. Paper documents should be shredded and data on CDs or memory sticks or similar must be rendered permanently unreadable."
        ]
      },
      {
        heading: "Data Protection Impact Assessments",
        content: [
          "36. Some of the Processing that the Employer carries out may result in risks to privacy.",
          "",
          "37. Where Processing would result in a high risk to staff rights and freedoms, the Employer will carry out a data protection impact assessment to determine the necessity and proportionality of Processing. This will include considering the purposes for which the activity is carried out, the risks for individuals, and the measures that can be put in place to mitigate those risks."
        ]
      },
      {
        heading: "Data Breaches",
        content: [
          "38. If we discover that there has been a breach of staff personal data that poses a risk to the rights and freedoms of individuals, we will report it to the ICO within 72 hours of discovery.",
          "",
          "39. We will record all data breaches regardless of their effect in accordance with our practices and procedures on data breaches. Contact Director for more information.",
          "",
          "40. If the breach is likely to result in a high risk to individuals' rights and freedoms, we will tell affected individuals that there has been a breach and provide them with more information about its likely consequences and the mitigation measures that have been taken."
        ]
      },
      {
        heading: "Individual Responsibilities",
        content: [
          "41. Staff are responsible for helping the Employer keep their Personal Data up to date.",
          "",
          "42. Staff should let the Employer know if Personal Data provided to the Employer changes, e.g. if you move house or change your bank details.",
          "",
          "43. You may have access to the Personal Data of other staff members and of our customers employment. Where this is the case, the Employer relies on staff members to help meet its data protection obligations to staff and to customers.",
          "",
          "44. Individuals who have access to Personal Data are required:",
          "   a. To access only personal data that they have authority to access and only for authorised purposes.",
          "   b. Not to disclose Personal Data except to individuals (whether inside or outside of the Employer) who have appropriate authorisation.",
          "   c. To keep Personal Data secure (e.g. by complying with rules on access to premises, computer access (including password protection, and secure file storage and destruction).",
          "   d. Not to remove Personal Data, or devices containing or that can be used to access Personal Data, from the Employer's premises without adopting appropriate security measures (such as encryption or password protection) to secure the data and the device.",
          "   e. Not to store Personal Data on local drives or on personal devices that are used for work purposes."
        ]
      },
      {
        heading: "Training",
        content: [
          "45. We will provide training to all individuals about their data protection responsibilities as part of their induction process and at appropriate and regular intervals thereafter.",
          "",
          "46. Individuals whose roles require regular access to Personal Data, or who are responsible for implementing this policy or responding to subject access requests under this policy, will receive additional training to help them understand their duties and how to comply with them."
        ]
      },
      {
        heading: "Employee Privacy Notice - Statement and Purpose",
        content: [
          "1. S.A.N. Security LTD (the Employer, we, our or us) collects and processes personal data relating to its employees in order to manage its relationship with them. We are committed to being transparent about how we collect and use that data and to meeting our data protection obligations."
        ]
      },
      {
        heading: "What Information Does the Employer Collect?",
        content: [
          "2. We collect and process a range of information about you. This includes:",
          "   a. your name, address and contact details, including email address and telephone number, date of birth and gender;",
          "   b. the terms and conditions of your employment;",
          "   c. details of your qualifications, skills, experience and employment history, including start and end dates, with previous employers and with us;",
          "   d. information about your remuneration, including entitlement to benefits, such as pensions and insurance cover;",
          "   e. details of your bank account and national insurance number;",
          "   f. information about your marital status, next of kin, dependents and emergency contacts;",
          "   g. information about your nationality and entitlement to work in the UK;",
          "   h. details of your schedule (days of work and working hours) and attendance at work;",
          "   i. details of periods of leave taken by you, including holiday, sickness absence, family leave and sabbaticals and the reasons for the leave;",
          "   j. photographs or videos;",
          "   k. details of any disciplinary or grievance procedures in which you have been involved, including any warnings issued to you and related correspondence; and",
          "   l. information about medical or health conditions, including whether or not you have a disability for which we need to make reasonable adjustments.",
          "",
          "3. We may collect this information in a variety of ways. For example, data might be collected through application forms, CVs or resumes; obtained from your passport; from forms completed by you at the start of or during employment (such as benefit nomination forms); from correspondence with you; or through interviews, meetings or other assessments.",
          "",
          "4. In some cases, we may collect personal data about you from third parties, such as references supplied by former employers.",
          "",
          "5. Data will be stored in a range of different places, including in your electronic personnel file, in our HR management systems and in other IT systems (including our email system)."
        ]
      },
      {
        heading: "Why Does the Employer Process Personal Data?",
        content: [
          "6. We need to process your personal data to enter into an employment contract with you and to meet our obligations under your employment contract. For example, we need to process your data to provide you with an employment contract, to pay you in accordance with your employment contract and to administer benefits, pension and insurance entitlements.",
          "",
          "7. In some cases, we need to process data to ensure that we are complying with our legal obligations. For example, we are required to check an employee's entitlement to work in the UK, to deduct tax, to comply with health and safety laws and to enable employees to take periods of leave to which they are entitled.",
          "",
          "8. In other cases, we have a legitimate interest in processing personal data before, during and after the end of the employment relationship. Processing employee data allows us to:",
          "   a. run recruitment and promotion processes;",
          "   b. maintain accurate and up-to-date employment records and contact details (including details of who to contact in the event of an emergency), and records of employee contractual and statutory rights;",
          "   c. operate and keep a record of employee performance and related processes to confirm compliance with our internal policies and procedures, to plan for career development, and for succession planning and workforce management purposes;",
          "   d. operate and keep a record of other types of leave (including maternity, paternity, adoption, parental and shared parental leave), to allow effective workforce management, to ensure that we comply with duties in relation to leave entitlement, and to ensure that employees are receiving the pay or other benefits to which they are entitled;",
          "   e. ensure effective general HR and business administration;",
          "   f. provide references on request for current or former employees; and",
          "   g. respond to and defend against legal claims.",
          "",
          "9. Some special categories of personal data, i.e. sensitive personal data, such as information about health or medical conditions, are processed to carry out employment law obligations (such as those in relation to employees with disabilities).",
          "",
          "10. We also collect information relating to your sickness records to maintain a record of your sickness absence and copies of any doctor's notes or other documents supplied to us in connection with your health, to inform your colleagues and others that you are absent through sickness as reasonably necessary to manage your absence, to deal with unacceptably high or suspicious sickness absence and to inform reviewers for appraisal purposes of your sickness absence levels.",
          "",
          "11. Where we process other special categories of personal data, such as information about ethnic origin, sexual orientation or religion or belief, this is done for the purposes of carrying out our legal obligations and exercising specific legal rights in relation to employment."
        ]
      },
      {
        heading: "Who Has Access to Data?",
        content: [
          "12. Your information may be shared internally, including with members of the HR and recruitment team (including payroll), your line manager, managers in the business area in which you work and IT staff if access to the data is necessary for the performance of their roles.",
          "",
          "13. We share your data with third parties in order to:",
          "   a. obtain advice from professional advisers, including accountants, auditors, lawyers, insurers, bankers, and others;",
          "   b. help third party service providers who provide products and services to us such as payroll, pension scheme and benefits administration, human resources, performance management, training, expense management, IT, etc; and",
          "   c. facilitate the detection of crime or the collection of taxes or duties.",
          "",
          "14. We also share your data with third parties that process data on our behalf in connection with payroll and the provision of benefits.",
          "",
          "15. We may also disclose your personal data to third parties:",
          "   a. when we determine that disclosure is required to protect our rights, property, or personal safety, or to respond to requests by public, regulatory, or law enforcement authorities, including to meet national security or law enforcement requirements; or",
          "   b. if we sell some or all of our business or assets, we may disclose your personal data to the prospective seller or buyer of such business or assets, and if the transaction closes, then your personal data may be transferred to the buyer.",
          "",
          "16. If we transfer employee personal data to a third party vendor for processing (e.g. payroll services), we are responsible as the data controller for the processing of that data."
        ]
      },
      {
        heading: "Choice",
        content: [
          "17. We do not currently share your personal data with third parties other than our service providers who act on our behalf. However, if we decide to do so in the future, we will offer you the opportunity to choose (opt out) before your personal data is disclosed to a third party controller (i.e. a non-service provider). Also, if we decide to use your data for purposes that are different from the purpose(s) for which it was originally collected or subsequently authorised by you, we will offer you the opportunity to choose (opt out) before such use."
        ]
      },
      {
        heading: "How Does the Employer Protect Data?",
        content: [
          "18. We take the protection of your data seriously. We have internal policies and controls in place to try and ensure that your data is not lost, accidentally destroyed, misused or disclosed and is not accessed except by our employees in the performance of their duties.",
          "",
          "19. Where we engage third parties to process personal data on our behalf, we do so on the basis of written instructions, and such third parties are under a duty of confidentiality and are obliged to implement appropriate technical and organisational measures to ensure the security of data."
        ]
      },
      {
        heading: "For How Long Does the Employer Keep Data?",
        content: [
          "20. We will hold your data for the duration of your employment. The periods for which your data will be held after the end of employment are set out in the Employer's policies and procedures on data retention."
        ]
      },
      {
        heading: "Your Rights (Privacy Notice)",
        content: [
          "21. As a data subject, you have a number of rights. You can:",
          "   a. access and obtain a copy of your personal data on request;",
          "   b. require us to change incorrect or incomplete personal data;",
          "   c. require us to delete or stop processing your data in certain circumstances such as where the data is no longer necessary for the purposes of processing;",
          "   d. object to the processing of your data where we are relying on our legitimate interests as the legal ground for processing, in certain circumstances; and",
          "   e. ask us to stop processing data for a period if data is inaccurate or there is a dispute about whether or not your interests override our legitimate grounds for processing data.",
          "",
          "22. If you would like to exercise any of these rights, please contact your line manager or the HR Department."
        ]
      },
      {
        heading: "Complaint Resolution",
        content: [
          "23. If you believe that we have not complied with this privacy notice or your data protection rights, you have the right to file a complaint with the UK Information Commissioner's Office (ICO) (at www.ico.org.uk/make-a-complaint/), however, we hope that you will attempt to resolve the complaint with us first. In addition, If you have any inquiries or complaints about the handling of your personal data, or about our privacy practices generally, please contact us at contact@serviceallnations.org.uk and we will respond to your inquiry promptly."
        ]
      },
      {
        heading: "What if You Do Not Provide Personal Data?",
        content: [
          "24. You have some obligations under your employment contract to provide us with certain personal data. In particular, you are required to report absences from work and may be required to provide information about disciplinary or other matters under the implied duty of good faith. You may also have to provide us with data in order to exercise your statutory rights, such as in relation to statutory leave entitlements. Failing to provide the data may mean that you are unable to exercise your statutory rights.",
          "",
          "25. Certain information, such as contact details, your right to work in the UK and payment details, have to be provided to enable us to enter a contract of employment with you. If you do not provide other information, this will hinder our ability to administer the rights and obligations arising as a result of the employment relationship efficiently."
        ]
      }
    ]
  },
  {
    id: 2,
    title: "Confidentiality Policy",
    sections: [
      {
        heading: "1. Purpose",
        content: [
          "S.A.N. Security Ltd is committed to protecting all confidential and sensitive information relating to our clients, operations, staff, and business activities."
        ]
      },
      {
        heading: "2. Scope",
        content: [
          "This policy applies to all employees, directors, contractors, subcontractors, agency staff, and temporary workers."
        ]
      },
      {
        heading: "3. Definition of Confidential Information",
        content: [
          "Confidential information includes client details, security procedures, incident reports, personal data, commercial and financial information."
        ]
      },
      {
        heading: "4. Staff Responsibilities",
        content: [
          "Staff must protect confidential information, use it only for business purposes, and not disclose it to unauthorised persons."
        ]
      },
      {
        heading: "5. Data Protection",
        content: [
          "All personal data must be handled in accordance with UK GDPR and the Data Protection Act 2018."
        ]
      },
      {
        heading: "6. Disclosure of Information",
        content: [
          "Disclosure is permitted only with written authorisation or where required by law."
        ]
      },
      {
        heading: "7. End of Employment",
        content: [
          "Confidentiality obligations continue after employment ends. All company materials must be returned."
        ]
      },
      {
        heading: "8. Breach of Policy",
        content: [
          "Breaches may result in disciplinary action, including dismissal."
        ]
      },
      {
        heading: "9. Review",
        content: [
          "This policy will be reviewed regularly."
        ]
      }
    ]
  },
  {
    id: 3,
    title: "Anti Bribery & Corruption Policy",
    sections: [
      {
        heading: "Purpose",
        content: [
          "S.A.N. Security Ltd is committed to conducting business ethically and with integrity and adopts a zero-tolerance approach to bribery and corruption."
        ]
      },
      {
        heading: "Scope",
        content: [
          "This policy applies to all directors, employees, contractors, and representatives."
        ]
      },
      {
        heading: "Prohibited Conduct",
        content: [
          "Offering, giving, receiving, or soliciting bribes or improper advantages is strictly prohibited."
        ]
      },
      {
        heading: "Gifts & Hospitality",
        content: [
          "Any gifts or hospitality must be reasonable, transparent, and not intended to influence business decisions."
        ]
      },
      {
        heading: "Reporting",
        content: [
          "Concerns should be reported to management."
        ]
      },
      {
        heading: "Consequences",
        content: [
          "Breaches of this policy may result in disciplinary action."
        ]
      },
      {
        heading: "Policy Review",
        content: [
          "This policy is reviewed periodically."
        ]
      }
    ]
  },
  {
    id: 4,
    title: "Health and Safety Policy",
    sections: [
      {
        heading: "Purpose of Policy",
        content: [
          "S.A.N. Security LTD (the Employer, we, our or us) takes health and safety issues seriously and is committed to protecting the health and safety of its staff and all those affected by its business activities and attending its premises.",
          "",
          "This policy is intended to help the Employer achieve this by clarifying who is responsible for health and safety matters and what their responsibilities are.",
          "",
          "This is a statement of policy only and does not form part of your contract of employment. This policy may be amended at any time by the Employer at its absolute discretion.",
          "",
          "The Employer will review this policy at regular intervals to ensure that it is achieving its aims effectively."
        ]
      },
      {
        heading: "Who Is Responsible for Workplace Health and Safety?",
        content: [
          "3. Achieving a healthy and safe workplace is a collective task shared between the Employer and staff. This policy and the rules contained in it apply to all staff of the Employer, irrespective of seniority, tenure, and working hours, including all employees, directors and officers, consultants and contractors, casual or agency staff, trainees, homeworkers, fixed-term staff and any volunteers. Specific responsibilities of staff are set out in the section headed \"Responsibilities of all staff\" below."
        ]
      },
      {
        heading: "Employer Responsibilities",
        content: [
          "4. The Employer is responsible for:",
          "   a. Taking reasonable steps to safeguard the health and safety of staff, people affected by the Employer's business activities, and people visiting its premises.",
          "   b. Identifying health and safety risks and finding ways to manage or overcome them.",
          "   c. Providing a safe and healthy place of work and safe entry and exit arrangements, including during an emergency situation.",
          "   d. Providing and maintaining safe working areas, equipment and systems and, where necessary, appropriate protective clothing.",
          "   e. Providing safe arrangements for the use, handling, storage and transport of articles and substances.",
          "   f. Providing adequate information, instruction, training and supervision to enable all staff to do their work safely, to avoid hazards and to contribute positively to their own health and safety at work. The Employer will give you the opportunity to ask questions and advise who best to contact in respect if you are unsure about how to safely carry out your work.",
          "   g. Ensuring any health and safety representatives receive appropriate training to carry out their functions effectively.",
          "   h. Providing a health and safety induction and appropriate safety training to your role, including the use of personal protective equipment (PPE).",
          "   i. Promoting effective communication and consultation between the Employer and staff concerning health and safety matters.",
          "   j. If an epidemic or pandemic alert is issued, providing instructions, arrangements and advice to staff as to the organisation of business operations and steps to be taken to minimise the risk of infection.",
          "   k. Regularly monitoring and reviewing the management of health and safety at work, making any necessary changes, and bringing those to the attention of all staff.",
          "",
          "5. Overall responsibility for health and safety lies with Mr Iulian Pana. They have appointed Mr Iulian Pana as the Health and Safety Officer with day-to-day responsibility for health and safety matters.",
          "",
          "6. Any concerns about health and safety matters should be communicated to the Health and Safety Officer."
        ]
      },
      {
        heading: "Responsibilities of All Staff - General Staff Responsibilities",
        content: [
          "7. All staff must:",
          "   a. Take reasonable care for their own health and safety and that of others who may be affected by their acts or omissions.",
          "   b. Co-operate with the Health and Safety Officer and the Employer generally to enable compliance with health and safety duties and requirements.",
          "   c. Comply with any health and safety instructions and rules, including instructions on the safe use of equipment.",
          "   d. Keep health and safety issues in the front of their minds and take personal responsibility for the health and safety implications of their own acts and omissions.",
          "   e. Keep the workplace tidy and hazard-free.",
          "   f. Report all health and safety concerns to the Health and Safety Officer promptly, including any potential risks, hazards or malfunctioning of equipment, however minor or trivial they may seem.",
          "   g. Co-operate in the Employer's investigation of any incident or accident which either has led to injury or which, in the Employer's opinion, could have led to injury."
        ]
      },
      {
        heading: "Staff Responsibilities Relating to Equipment",
        content: [
          "8. All staff must:",
          "   a. Use equipment as directed, following any instructions given by representatives of management or contained in any written operating manual or instructions for use, and adhering to any relevant training.",
          "   b. Report any fault with, damage to, or concern about any equipment (including health and safety equipment) or its use to the Health and Safety Officer, who is responsible for maintenance and safety of equipment.",
          "   c. Ensure that health and safety equipment is not interfered with.",
          "   d. Not attempt to repair equipment unless suitably trained and authorised."
        ]
      },
      {
        heading: "Staff Responsibilities Relating to Accidents and First Aid",
        content: [
          "9. All staff must:",
          "   a. Promptly report any accident at work involving personal injury, however trivial, to the Health and Safety Officer so that details can be recorded in the Accident Book. They must also cooperate with any associated investigation.",
          "   b. Familiarise themselves with the details of first aid facilities and trained first aiders, which are available from the Health and Safety Officer.",
          "   c. If an accident occurs, dial 07456887019 and ask for the duty first aider, giving name, location and brief details of the problem.",
          "   d. The Health and Safety Officer is responsible for investigating any injuries or work-related illnesses, preparing and keeping accident records, and for submitting reports under the Reporting of Injuries, Diseases and Dangerous Occurrences Regulations 2013 (RIDDOR), where required."
        ]
      },
      {
        heading: "Staff Responsibilities Relating to National Health Alerts",
        content: [
          "10. If an epidemic or pandemic alert is issued, all staff must comply and co-operate with all instructions, arrangements and advice issued by the Employer as to the organisation of business operations and steps to be taken by staff to minimise the risk of infection. Any questions should be referred to the Health and Safety Officer.",
          "",
          "11. Given the outbreak of Coronavirus (COVID-19), it is important that all staff members follow the guidelines set out in this policy to ensure maximum safety and to minimise the risk of infection. We will review these guidelines regularly to ensure they are kept up-to-date with Government guidance."
        ]
      },
      {
        heading: "Staff Responsibilities Relating to Emergency Evacuation and Fire",
        content: [
          "12. All staff must:",
          "   a. Familiarise themselves with the instructions about what to do if there is a fire which are available from the Health and Safety Officer.",
          "   b. Ensure they are aware of the location of fire extinguishers, fire exits and alternative ways of leaving the building in an emergency.",
          "   c. Comply with the instructions of fire wardens if there is a fire, suspected fire or fire alarm (or a practice drill for any of these scenarios).",
          "   d. Co-operate in fire drills and take them seriously (ensuring that any visitors to the building do the same). Fire drills will be held at least once every 12 months.",
          "   e. Ensure that fire exits or fire notices or emergency exit signs are not obstructed or hidden at any time.",
          "   f. Notify the Health and Safety Officer immediately of any circumstances (for example, impaired mobility) which might hinder or delay evacuation in a fire. This will allow the Health and Safety Officer to discuss a personal evacuation plan for you, which will be shared with the fire wardens and colleagues working near to you.",
          "",
          "13. On discovering a fire, all staff must:",
          "   a. Immediately trigger the nearest fire alarm and, if time permits, call Director and notify the location of the fire.",
          "   b. Attempt to tackle the fire ONLY if they have been trained or otherwise feel competent to do so. Nominated members of staff will be trained in the use of fire extinguishers.",
          "",
          "14. On hearing the fire alarm, all staff must:",
          "   a. Remain calm and immediately evacuate the building, walking quickly without running, and following any instructions of the fire wardens.",
          "   b. Leave without stopping to collect personal belongings.",
          "   c. Stay out of any lifts.",
          "   d. Remain out of the building until notified by a fire warden that it is safe to re-enter.",
          "",
          "15. The Health and Safety Officer is responsible for ensuring that fire risk assessments take place, that changes are made where required, and for making sure there are regular checks of fire extinguishers, fire alarms, escape routes, signage and emergency lighting."
        ]
      },
      {
        heading: "Risk Assessments and Manual Handling",
        content: [
          "16. Risk assessments are essentially a careful examination of what in the workplace could cause harm to people. The Employer will assess any risks and consider measures to best minimise any risk. The Employer will carry out general workplace risk assessments when required or as reasonably requested by staff. Managers must ensure that any necessary risk assessments take place and the resulting recommendations are implemented. The Health and Safety Officer is responsible for workplace risk assessments and any measures to control risks.",
          "",
          "17. Personal Protective Equipment (PPE) is provided where risks cannot be otherwise effectively controlled.",
          "",
          "18. Guidance on manual handling (for example, lifting and carrying heavy objects) can be obtained from the Health and Safety Officer and where necessary training will be provided by the Employer, but the Employer will try to minimise or avoid the need for manual handling where there is a risk of injury."
        ]
      },
      {
        heading: "Display Screen Equipment (DSE)",
        content: [
          "19. The Employer is obliged to ensure that:",
          "   a. Risks to health and safety from DSE use (such as musculoskeletal disorders, visual fatigue and mental stress) are controlled.",
          "   b. Staff are aware of the potential risks to their health and safety from DSE use and the actions they can take to reduce these risks.",
          "",
          "20. Further guidance on the use of display screen equipment can be obtained from the Health and Safety Officer."
        ]
      },
      {
        heading: "DSE - Employer Responsibilities",
        content: [
          "21. The Employer will:",
          "   a. Ensure DSE Assessments are carried out on each workstation and include the display screen equipment, furniture and working environment.",
          "   b. Where health and safety issues have been highlighted in the DSE Assessment, ensure that appropriate remedial action is taken to reduce any identified risks.",
          "   c. Maintain records of all DSE Assessments and risk assessments.",
          "   d. Encourage the early reporting by staff of any symptoms which may be related to visual display screen work.",
          "   e. In circumstances where an injury or ill health associated with DSE is identified, ensure that an incident or accident report is completed.",
          "   f. Plan the activities of users of DSE so that short, frequent breaks are taken to prevent intensive periods of on-screen activity."
        ]
      },
      {
        heading: "DSE - Staff Responsibilities",
        content: [
          "22. Staff will:",
          "   a. Cooperate with the completion of the workstation DSE assessment and all measures/training given to promote safe working practice.",
          "   b. Use equipment in the intended manner.",
          "   c. Adopt any advice given by the Employer to prevent intensive periods of on-screen activity.",
          "   d. Use any corrective glasses prescribed specifically for working with DSE.",
          "   e. Inform their line manager immediately if they experience any problems or ill-health which could affect their ability to work with DSE."
        ]
      },
      {
        heading: "Workstation Assessments",
        content: [
          "23. Workstation assessments must be carried out on each workstation. Responsibility for ensuring workstation assessments are carried out lies with the Health and Safety Officer.",
          "",
          "24. As a first step, staff must complete a DSE self-assessment.",
          "",
          "25. DSE self-assessments should be carried out on:",
          "   a. New staff at induction.",
          "   b. Laptop users.",
          "   c. Homeworkers.",
          "",
          "26. Staff should review their self-assessment annually, or when there are significant changes to their workstation."
        ]
      },
      {
        heading: "Breaks",
        content: [
          "27. Staff are encouraged and will be expected to take opportunities for breaks in their work routine to prevent the onset of fatigue. See guidance for more information, or speak with the Health and Safety Officer."
        ]
      },
      {
        heading: "Eye Tests",
        content: [
          "28. Staff are entitled to eye tests by a registered practitioner (Optician or Doctor) on the following occasions:",
          "   a. When they first become a user of DSE.",
          "   b. When requested by staff themselves.",
          "   c. At regular intervals thereafter on the recommendation of the practitioner (usually every 2 years).",
          "   d. When staff experience visual difficulties attributed to display screen use.",
          "",
          "29. Please note that glasses are solely and specifically for DSE use, and cannot be combined with lenses for other uses, be driving.",
          "",
          "30. For more information, please contact the Health and Safety Officer.",
          "",
          "31. Eye Testing Procedure:",
          "   a. You are responsible for arranging your own appointment with the practitioner."
        ]
      },
      {
        heading: "Non-Compliance With Health and Safety Rules",
        content: [
          "32. Any breach of health and safety rules or failure to comply with this policy will be taken very seriously and is likely to result in disciplinary action against the offender, in accordance with the Employer's disciplinary policy, up to and including immediate dismissal."
        ]
      }
    ]
  },
  {
    id: 5,
    title: "Equal Opportunities Policy",
    sections: [
      {
        heading: "Statement of Policy and Purpose of Policy",
        content: [
          "1. S.A.N. Security LTD (the Employer, we, our or us) is committed to equal opportunities for all staff and applicants.",
          "",
          "2. It is our policy that all employment decisions are based on merit and the legitimate business needs of the organisation. We do not discriminate on the basis of race, colour or nationality, ethnic or national origins, sex, gender reassignment, sexual orientation, marital or civil partner status, pregnancy or maternity, disability, religion or belief, age or any other ground on which it is or becomes unlawful to discriminate under the laws of England, Wales and Scotland (referred to as Protected Characteristics).",
          "",
          "3. Our intention is to enable all our staff to work in an environment which allows them to fulfil their potential without fear of discrimination, harassment or victimisation. Our commitment to equal opportunities extends to all aspects of the working relationship including:",
          "   a. recruitment and selection procedures;",
          "   b. terms of employment, including pay, conditions and benefits;",
          "   c. training, appraisals, career development and promotion;",
          "   d. work practices, conduct issues, allocation of tasks, discipline and grievances;",
          "   e. work-related social events; and",
          "   f. termination of employment and matters after termination, including references.",
          "",
          "4. This policy is intended to help us achieve our diversity and anti-discrimination aims by clarifying the responsibilities and duties of all staff in respect of equal opportunities and discrimination. We will promote effective communication and consultation between us and staff concerning equal opportunities by means it considers appropriate.",
          "",
          "5. The principles of non-discrimination and equal opportunities also apply to the way in which staff treat visitors, clients, customers, suppliers and former staff members.",
          "",
          "6. This is a statement of policy only and does not form part of your contract of employment. This policy may be amended at any time by us, at our absolute discretion."
        ]
      },
      {
        heading: "Who Is Responsible for Equal Opportunities?",
        content: [
          "7. Achieving an equal opportunities workplace is a collective task shared between us and all our staff. This policy and the rules contained in it, therefore, apply to all staff irrespective of seniority, tenure and working hours, including all employees, directors and officers, consultants and contractors, casual or agency staff, trainees, homeworkers and fixed-term staff and any volunteers or interns (referred to as Staff).",
          "",
          "8. The Board of Directors of the Employer has overall responsibility for this policy and for equal opportunities and discrimination law compliance in the workplace and Iulian Pana has been appointed as the person with day-to-day operational responsibility for these matters.",
          "",
          "9. All Staff have personal responsibility to ensure compliance with this policy, to treat colleagues with dignity at all times and not to discriminate against or harass other members of Staff, visitors, clients, customers, suppliers and former staff members. In addition, Staff who take part in management, recruitment, selection, promotion, training and other aspects of career development (referred to as Managers) have special responsibility for leading by example and ensuring compliance.",
          "",
          "10. Managers will receive appropriate training in equal opportunities and must take all necessary steps to:",
          "   a. promote the objective of equal opportunities and the values set out in this policy;",
          "   b. ensure that their own behaviour and those of the Staff they manage complies in full with this policy; and",
          "   c. ensure that any complaints of discrimination, victimisation or harassment (including against themselves) are dealt with appropriately and are not suppressed or disregarded."
        ]
      },
      {
        heading: "What is Discrimination?",
        content: [
          "11. Discrimination occurs in different ways, some more obvious than others. Discrimination on the grounds of any of the Protected Characteristics is prohibited by law, even if unintentional, unless a particular exception applies."
        ]
      },
      {
        heading: "Direct Discrimination",
        content: [
          "12. Direct discrimination is less favourable treatment because of one of the Protected Characteristics. Examples would include refusing a woman a job as a chauffeur because you believe that women are not good drivers or restricting recruitment to persons under 40 because you perceive that a younger workforce to be more energetic or dynamic.",
          "",
          "13. Direct discrimination can arise in some cases even though the person complaining does not actually possess the Protected Characteristic but is perceived to have it or associates with other people who do. For example, when a person is less favourably treated because they are (wrongly) believed to be homosexual or because they have a spouse who is Muslim."
        ]
      },
      {
        heading: "Indirect Discrimination",
        content: [
          "14. Indirect discrimination arises when an employer applies an apparently neutral provision, criterion or practice which in fact puts individuals with a particular Protected Characteristic at a disadvantage, statistically and this is unjustified. To show discrimination the individual complaining also has to be personally disadvantaged. An example would be a requirement for job candidates to have 10 years' experience in a particular role, since this will be harder for young people to satisfy. This kind of discrimination is unlawful unless it is a proportionate means of achieving a legitimate aim."
        ]
      },
      {
        heading: "Victimisation",
        content: [
          "15. Victimisation means treating a person less favourably because they have made a complaint of discrimination or have provided information in connection with a complaint or because they might do one of these things."
        ]
      },
      {
        heading: "Harassment",
        content: [
          "16. Harassment is defined as unwanted conduct related to a relevant Protected Characteristic (within the Equality Act 2010) which has the effect of violating an individual's dignity or creating an intimidating, hostile, degrading, humiliating or offensive environment for that individual.",
          "",
          "17. Unlawful harassment may involve conduct of a sexual nature or it may be related to age, race, colour or nationality, ethnic or national origins, sex, gender reassignment, sexual orientation, disability, religion or belief, pregnancy or maternity.",
          "",
          "18. Harassment can arise in some cases even though the person complaining does not actually possess a Protected Characteristic but is perceived to have it (for example, when a person is harassed because they are (wrongly) believed to be homosexual) or associates with other people who possess a Protected Characteristic (for example, because they have a spouse who is Muslim).",
          "",
          "19. A person may also be subject to harassment even if they were not the intended target. For example, a person may be harassed by a sexist joke about a different gender if it created an offensive environment for them to work in.",
          "",
          "20. Harassment may include:",
          "   a. use of insults or slurs based on a Protected Characteristic or of a sexual nature or other verbal abuse or derogatory, offensive or stereotyping jokes or remarks;",
          "   b. physical or verbal abuse, threatening or intimidating behaviour because of a Protected Characteristic or behaviour of a sexual nature;",
          "   c. unwelcome physical contact including touching, hugging, kissing, pinching or patting, brushing past, invading personal space, pushing grabbing or other assaults;",
          "   d. mocking, mimicking or belittling a person's disability, appearance, accent or other personal characteristics;",
          "   e. unwelcome requests for sexual acts or favours; verbal sexual advances, vulgar, sexual, suggestive or explicit comments or behaviour;",
          "   f. repeated requests, either explicitly or implicitly, for dates;",
          "   g. repeated requests for social contact or after it has been made clear that requests are unwelcome;",
          "   h. comments about body parts or sexual preference;",
          "   i. displaying or distributing offensive or explicit pictures, items or materials relating to a Protected Characteristic or of a sexual nature;",
          "   j. shunning or ostracising someone, for example, by deliberately excluding them from conversations or activities;",
          "   k. 'outing' or threatening to 'out' someone's sexual orientation (i.e. to make it known);",
          "   l. explicit or implicit suggestions that employment status or progression is related to toleration of, or acquiescence to sexual advances, or other behaviour amounting to harassment;",
          "   m. racists, sexist, homophobic or ageist jokes, and stereotypical remarks about a particular ethnic or religious group or gender;",
          "   n. posters, graffiti, obscene gestures, flags and emblems; and",
          "   o. isolation from normal work or study places, conversations or social events.",
          "",
          "21. Other important points to note about harassment:",
          "   a. a single incident can amount to harassment;",
          "   b. behaviour that has continued for a long period without complaint can amount to harassment;",
          "   c. it is not necessary for an individual to intend to harass someone for their behaviour to amount to harassment;",
          "   d. it is not necessary for an individual to communicate that behaviour is unwelcome before it amounts to harassment; and",
          "   e. the onus is on each individual to be certain that their behaviour and conduct is appropriate and is not unwanted and in the case of doubt, you must refrain from such conduct."
        ]
      },
      {
        heading: "Disability Discrimination",
        content: [
          "22. This could be direct or indirect discrimination, and is any unjustified less favourable treatment because of the effects of a disability, and failure to make reasonable adjustments to alleviate disadvantages caused by a disability."
        ]
      },
      {
        heading: "Disabled Persons",
        content: [
          "23. Any Staff member who considers that they may have a disability is strongly encouraged to speak with the company management particularly if they experience difficulties at work because of their disability so that any reasonable adjustments to help overcome or minimise difficulties can be discussed. For these purposes, disability includes any physical or mental impairment which substantially affects your ability to perform day-to-day activities and has lasted (or is likely to last) more than 12 months. Disclosure of this information will be treated in confidence, if you wish it to be, so far as is reasonably practicable and we will do our best to handle matters sensitively and to ensure that you are treated with dignity and with respect for your privacy.",
          "",
          "24. We will consult with you about whether adjustments are needed to avoid you being disadvantaged and may ask you to see a doctor appointed by us, to advise on this. We will seek to accommodate your needs within reason. If we consider a particular adjustment unreasonable we will explain why and try to find an alternative solution.",
          "",
          "25. Managers with responsibility for managing a member of Staff who they know or think to be disabled should speak to Iulian Pana to ensure that all relevant duties are complied with."
        ]
      },
      {
        heading: "Making Employment Decisions Fairly",
        content: [
          "26. As noted above, the Employer will recruit employees and make other employment decisions concerning promotion, training, dismissal and related issues on the basis of objective criteria.",
          "",
          "27. Managers should only stipulate criteria or conditions for employment decisions (including job selection, promotion and redundancy) which are based on a legitimate business need and which do not go further than is needed to satisfy that need. If you are in any doubt about whether particular criteria or conditions are indirectly discriminatory or justifiable, then please speak to company management."
        ]
      },
      {
        heading: "Recruitment",
        content: [
          "28. Managers involved in recruitment must:",
          "   a. specify only recruitment criteria that are relevant to the job, reflect genuine business needs and are proportionate. More than one person should be involved in shortlisting of applicants wherever practicable;",
          "   b. ensure that vacancies are advertised to a diverse audience and try to avoid informal recruitment methods that exclude fair competition. In very rare cases, it may be legitimate and necessary to restrict recruitment to a particular role to certain groups, but it is essential that this is discussed with the management so that appropriate steps can be taken to ensure legality;",
          "   c. review job advertisements carefully to ensure that stereotyping is avoided and that particular groups are not unjustifiably discouraged from applying;",
          "   d. not ask applicants about health or disability before a job offer is made (other than in exceptional circumstances and after having been approved by the management. If necessary a job offer can be expressed to be conditional upon satisfactorily passing a medical check;",
          "   e. not ask candidates about any Protected Characteristic if the question may demonstrate an intention to discriminate. For example, candidates should not be asked about current or future pregnancy, childcare or related matters;",
          "   f. not make assumptions about immigration status based on appearance, accent or apparent nationality; and",
          "   g. so far as reasonably practicable, keep a written record of their reasons for relevant decisions.",
          "",
          "29. The Employer is legally required to verify that all employees have the right to work in the UK. Prior to starting employment, all employees must produce original documents to the Employer's satisfaction, irrespective of nationality. Information about the documents required is available from the company management."
        ]
      },
      {
        heading: "Staff Training, Career Development and Promotion",
        content: [
          "30. Training needs may be identified during the normal appraisal process. Appropriate training to facilitate progression will be accessible to all staff.",
          "",
          "31. All promotion decisions will be made on the basis of merit and according to proportionate criteria determined by legitimate business need.",
          "",
          "32. Staff diversity at different levels of the organisation will be kept under review to ensure equality of opportunity. Where unjustified barriers to progression are identified, these will be removed."
        ]
      },
      {
        heading: "Conditions of Service",
        content: [
          "33. Access to benefits and facilities and terms of employment will be kept under review to ensure that they are appropriately structured and that no unlawful barriers to qualification or access exist."
        ]
      },
      {
        heading: "Discipline and Termination of Employment",
        content: [
          "34. Any redundancy selection criteria and procedures that are used, or other decisions taken to terminate employment, will be fair and not directly or indirectly discriminatory.",
          "",
          "35. Disciplinary procedures and penalties will be applied without discrimination, whether they result in disciplinary warnings, dismissal or other disciplinary action.",
          "",
          "36. Part-time and fixed-term staff will be treated the same as full-time or permanent staff of the same position and enjoy no less favourable terms and conditions (pro-rata, where appropriate), unless different treatment is justified."
        ]
      },
      {
        heading: "What to Do if You Encounter Discrimination",
        content: [
          "37. If you believe that you have been the victim of discrimination, you should follow the Employer's Grievance Procedure contained within this Staff Handbook.",
          "",
          "38. Every member of Staff has a responsibility to combat discrimination if they encounter it. Staff who observe or are aware of acts that they believe amount to discrimination directed at others are encouraged to report these to the company management.",
          "",
          "39. Any grievance or report raised about discrimination will be kept confidential so far as this is practicable. We may ask you if you wish your complaint(s) to be put to the alleged discriminator if disciplinary action appears to be appropriate. It sometimes may be necessary to disclose the complaint or take action even if this is not in line with your wishes, but we will seek to protect you from victimisation and, if you wish, we will seek to protect your identity. You should be aware that disciplinary action may be impossible without your co-operation or if you refuse to allow relevant information to be disclosed.",
          "",
          "40. Staff who raise a complaint about or report discrimination in good faith will be protected from retaliation or victimisation. As long as you act in good faith, the fact that you have raised a complaint or report will not affect your position within the Employer, even if the complaint is not upheld. Making a false allegation deliberately and in bad faith is a misconduct offence and will be dealt with in accordance with our Disciplinary Procedure contained within this Staff Handbook. Any member of Staff who attempts acts of retaliation or victimisation may be subject to disciplinary action up to and including summary dismissal for gross misconduct.",
          "",
          "41. If you make a complaint, it may be necessary to ask you to stay at home on paid leave while investigations are being conducted and the matter is being dealt with through the appropriate procedure. This may particularly be necessary in cases of alleged harassment."
        ]
      },
      {
        heading: "Non-Compliance With Equal Opportunities Rules",
        content: [
          "42. Any breach of equal opportunities rules or failure to comply with this policy will be taken very seriously and is likely to result in disciplinary action against the offender, up to and including immediate dismissal.",
          "",
          "43. Staff should also note that:",
          "   a. in some cases, they may be personally liable for their acts of discrimination and that legal action may be taken against them directly by the victim of any discrimination; and",
          "   b. it may be a criminal offence intentionally to harass another employee."
        ]
      },
      {
        heading: "Review of This Policy",
        content: [
          "44. The Board of Directors of the Employer will keep this policy under review.",
          "",
          "45. The Employer encourages Staff to comment on this policy and suggest ways in which it might be improved or ask any questions if they are unsure about any part of this policy or how it is applied by contacting the company management."
        ]
      }
    ]
  },
  {
    id: 6,
    title: "Anti Harassment and Bullying Policy",
    sections: [
      {
        heading: "Statement and Purpose of Policy",
        content: [
          "1. S.A.N. Security LTD (the Employer, we, our or us) is committed to providing a work environment free from harassment and bullying and ensuring that all staff are treated, and treat others, with dignity and respect.",
          "",
          "2. This policy does not form part of any employment contract and the Employer retains the right to amend it at any time, at our absolute discretion."
        ]
      },
      {
        heading: "What Does This Policy Cover?",
        content: [
          "3. This policy covers harassment, victimisation and bullying which takes place within and outside of the workplace, including on business trips, work-related social functions or events.",
          "",
          "4. This policy applies to all staff, irrespective of seniority, tenure and working hours, including all directors and officers, casual or agency staff, trainees, interns, fixed-term staff, volunteers, consultants and contractors. It also covers harassment and bullying by third parties, such as customers, suppliers or visitors to the business premises."
        ]
      },
      {
        heading: "What Are Harassment and Bullying?",
        content: [
          "5. Harassment is any behaviour as defined in the Section entitled 'Harassment' in the Employer's Equal Opportunities Policy contained in this Staff Handbook.",
          "",
          "6. Bullying is any behaviour, be it physical, verbal or non-verbal, that is offensive, intimidating, malicious or insulting and that involves a misuse of power (e.g. a position of authority or physical strength), which can result in a person feeling vulnerable, upset, humiliated, undermined or threatened.",
          "",
          "7. Examples of bullying include, but are not limited to:",
          "   a. unfair treatment;",
          "   b. inappropriate and/or derogatory remarks about a person's performance;",
          "   c. physical or psychological threats;",
          "   d. overbearing and intimidating levels of supervision;",
          "   e. abuse of authority or power by those in positions of seniority;",
          "   f. constantly changing targets in order to cause someone to fail;",
          "   g. making false allegations; and",
          "   h. deliberately excluding someone from meetings or communications without good reason.",
          "",
          "8. On their own, any reasonable, legitimate and constructive criticism or comments of a person's performance or behaviour, or reasonable instructions given in the courts of employment, will not amount to bullying."
        ]
      },
      {
        heading: "What is Victimisation?",
        content: [
          "9. Victimisation occurs where a member of staff is subjected to detrimental treatment because they have, in good faith, made an allegation of harassment, or has indicated an intention to make such an allegation, or has assisted or supported another person in bringing forward such an allegation, or participated in an investigation of a complaint, or participated in any disciplinary hearing arising from an investigation.",
          "",
          "10. We seek to protect all staff from victimisation arising as a result of bringing a complaint or assisting in an investigation where they act in good faith. Victimisation is a form of misconduct which may itself result in a disciplinary process."
        ]
      },
      {
        heading: "What if You Are Being Bullied or Harassed?",
        content: [
          "11. If you are being bullied or harassed, consider if you feel able to raise the problem informally with the person responsible. Clearly explain to them that their behaviour is unwanted and makes you feel uncomfortable. If you cannot speak to the responsible person (for example, because it is too difficult or embarrassing), speak to your line manager or the HR Department, who can provide confidential advice and assistance in resolving the issue formally or informally.",
          "",
          "12. If you are uncertain whether an incident or series of incidents amounts to bullying or harassment, contact your line manager or the HR Department for confidential advice.",
          "",
          "13. If your request is ignored, the bullying or harassment continues and/or you would prefer to take formal action, you should raise the matter formally under the complaint procedure set out below.",
          "",
          "14. A formal complaint about bullying or harassment should be made in writing and sent to the HR Department, identifying:",
          "   a. who has been bullying or harassing you;",
          "   b. the nature of the bullying or harassment;",
          "   c. the specific acts relied upon as constituting bullying or harassment;",
          "   d. when the alleged acts of bullying or harassment took place, including the dates and times where possible;",
          "   e. the names of any witnesses to any of the alleged acts of bullying or harassment; and",
          "   f. any action that has already been taken to attempt to stop the bullying or harassment from occurring (e.g. informally reporting it to your line manager).",
          "",
          "15. You will be invited to attend a meeting with the HR Department to discuss your complaint. You must make every effort to attend any scheduled meeting under this policy.",
          "",
          "16. You have the right to be accompanied by a companion to any meeting under this procedure. Your choice of companion will be agreed to if they are either a colleague, a trade union official or a trade union representative (which, if not an employed official, must be certified by their union as competent to accompany a worker) and under the circumstances, you have made a reasonable request to be accompanied.",
          "",
          "17. Your complaint will be investigated in a confidential and timely manner, by someone with appropriate experience and no prior involvement in the complaint, where possible. Details of the investigation, including the names of the person accused of bullying or harassment and the person making the complaint, will be disclosed on a \"need to know\" basis. We will also consider if any steps are necessary to manage the ongoing relationship and the person accused of bullying or harassment.",
          "",
          "18. When the investigation is completed, you will be informed of the Employer's decision. If we consider that you have been bullied or harassed by a staff member, we will deal with the matter under the Employer's Disciplinary Procedure contained in this Staff Handbook as a case of possible misconduct or gross misconduct. If we consider that you have been bullied or harassed by a third party, such as a customer or visitor, we will consider what actions will be appropriate to deal with the problem. If you are unhappy with the decision, you can raise an appeal under the formal appeal procedure set out in the section entitled 'Appeal' below.",
          "",
          "19. Regardless of whether your complaint is upheld, we will consider how best to manage any ongoing working relationship between you and the person concerned."
        ]
      },
      {
        heading: "Appeal",
        content: [
          "20. If you are unhappy with the decision and you wish to appeal, you should contact the HR Department within 10 working days of the date of the decision, saying that you disagree with the decision and giving your reason(s) why and, where relevant, providing any new evidence you seek to rely on.",
          "",
          "21. You will then be invited to an appeal hearing, normally within 5 working days of us receiving your letter of appeal.",
          "",
          "22. Your appeal will be heard by an impartial manager or if necessary an independent HR advisor who has not been part of the process up until the appeal stage. Your appeal will either be a review of your complaint or a complete rehearing, at the Employer's discretion.",
          "",
          "23. After the meeting, you will be given a decision, normally within 24 hours. The Employer's decision is final and there is no further right to appeal."
        ]
      },
      {
        heading: "Supporting and Protecting Those Involved",
        content: [
          "24. Staff who make complaints or who participate in good faith in any investigation under this policy must not suffer any form of victimisation or retaliation as a result. If you believe to have suffered such treatment, speak to your line manager or the HR Department. If the matter is not resolved or remedied, raise it formally under this policy, where appropriate.",
          "",
          "25. Anyone found to have victimised or retaliated against someone will be subject to disciplinary action in accordance with the Employer's Disciplinary Procedure.",
          "",
          "26. If an investigation under this policy concludes that a malicious or false claim of bullying or harassment has been made, the complainant may be subject to disciplinary action in accordance with the Employer's Disciplinary Procedure."
        ]
      },
      {
        heading: "Keeping Records",
        content: [
          "27. Information regarding any complaints made by or about a member of staff may be recorded on their personnel file, along with a record of the outcome and of any notes or other documents compiled during the process. Such data will be processed in accordance with the Employer's Data Protection and Data Security Policy which is contained in this Staff Handbook.",
          "",
          "28. For more information on how we use personal data, refer to the Employer's Employee Privacy Notice, which is contained in this Staff Handbook."
        ]
      }
    ]
  },
  {
    id: 7,
    title: "Personal Appearance and Hygiene Policy",
    sections: [
      {
        heading: "Purpose",
        content: [
          "S.A.N. Security Ltd requires all staff to maintain a professional appearance and good personal hygiene to ensure a positive image, workplace safety, and client confidence."
        ]
      },
      {
        heading: "Scope",
        content: [
          "This policy applies to all employees, contractors, and agency staff while on duty, representing the company, or attending assignments."
        ]
      },
      {
        heading: "General Standards",
        content: [
          "• Attend duty clean, presentable, and well-groomed",
          "• Wear clean, appropriate clothing and uniform (where issued)",
          "• Maintain good personal hygiene to avoid offensive body odour",
          "• Use deodorant or hygiene products where necessary",
          "• Ensure hair is clean and tidy",
          "• Maintain clean hands and tidy fingernails"
        ]
      },
      {
        heading: "Odour and Cleanliness",
        content: [
          "Staff must not attend duty with strong or offensive body odour, smell of alcohol, drugs, or excessive fragrance, or wear unclean clothing or uniforms."
        ]
      },
      {
        heading: "Uniform and Appearance",
        content: [
          "Uniforms must be worn as instructed, kept clean and presentable, and PPE must be worn where required."
        ]
      },
      {
        heading: "Cultural and Religious Considerations",
        content: [
          "The company respects cultural and religious practices and will consider reasonable adjustments where operationally possible."
        ]
      },
      {
        heading: "Management Action",
        content: [
          "Failure to comply may result in removal from duty or disciplinary action in line with company procedures."
        ]
      }
    ]
  },
  {
    id: 8,
    title: "Whistleblowing Policy",
    sections: [
      {
        heading: "Purpose",
        content: [
          "S.A.N. Security Ltd encourages staff to raise concerns about wrongdoing or unsafe practices without fear of retaliation."
        ]
      },
      {
        heading: "Scope",
        content: [
          "This policy applies to all staff, contractors, and associates."
        ]
      },
      {
        heading: "Reportable Concerns",
        content: [
          "Concerns may include unsafe practices, breaches of procedures, fraud, corruption, or misconduct."
        ]
      },
      {
        heading: "Raising a Concern",
        content: [
          "Concerns may be raised verbally or in writing directly with management."
        ]
      },
      {
        heading: "Confidentiality & Protection",
        content: [
          "Concerns are handled confidentially where possible, and no action will be taken against individuals raising genuine concerns."
        ]
      },
      {
        heading: "Investigation",
        content: [
          "All concerns are reviewed and investigated where appropriate."
        ]
      },
      {
        heading: "Policy Review",
        content: [
          "This policy is reviewed periodically."
        ]
      }
    ]
  },
  {
    id: 9,
    title: "Camping and Accommodation Policy",
    sections: [
      {
        heading: "1. Purpose",
        content: [
          "Where camping or accommodation is provided by S.A.N. Security Ltd, a client, or an event organiser, this policy sets out the standards of conduct, hygiene, responsibility, and cost arrangements expected from staff."
        ]
      },
      {
        heading: "2. Scope",
        content: [
          "This policy applies to all employees, contractors, and agency staff who use camping facilities or accommodation provided in connection with work assignments."
        ]
      },
      {
        heading: "3. General Standards",
        content: [
          "Staff must:",
          "• Use only allocated accommodation",
          "• Treat facilities with care and respect",
          "• Keep sleeping and shared areas clean and tidy",
          "• Follow site rules and management instructions",
          "• Behave professionally at all times"
        ]
      },
      {
        heading: "4. Hygiene & Cleanliness",
        content: [
          "Staff must maintain good personal hygiene, dispose of waste correctly, and keep shared spaces clean. Failure to comply may result in removal from accommodation."
        ]
      },
      {
        heading: "5. Noise, Behaviour & Alcohol",
        content: [
          "Excessive noise and antisocial behaviour are not permitted. Alcohol consumption must comply with company and site rules. Being under the influence of alcohol or drugs while on duty, on standby, or representing the company is strictly prohibited."
        ]
      },
      {
        heading: "6. Visitors",
        content: [
          "Visitors are not permitted in staff accommodation or camping areas unless explicitly authorised in advance by management."
        ]
      },
      {
        heading: "7. Health & Safety",
        content: [
          "Staff must follow all fire safety and emergency procedures, report hazards immediately, and must not use unsafe cooking, heating, or electrical equipment."
        ]
      },
      {
        heading: "8. Damage, Loss & Responsibility",
        content: [
          "Staff are responsible for any damage or loss caused intentionally or through negligence. The Company reserves the right to recover reasonable costs where appropriate."
        ]
      },
      {
        heading: "9. Accommodation Costs (When Provided by S.A.N. Security Ltd)",
        content: [
          "Where accommodation is provided directly by S.A.N. Security Ltd, the total cost may be shared equally between the number of staff occupying the accommodation.",
          "",
          "With prior written agreement, the individual staff member's share will be deducted from net wages.",
          "",
          "Any such deduction will be clearly communicated in advance, confirmed in writing, reasonable and proportionate, and will not result in pay falling below the National Minimum Wage.",
          "",
          "Staff may choose to decline company-provided accommodation."
        ]
      },
      {
        heading: "10. Management Action",
        content: [
          "Failure to comply with this policy may result in removal from accommodation or camping facilities, removal from the assignment, or disciplinary action in line with company procedures."
        ]
      }
    ]
  },
  {
    id: 10,
    title: "Use of Force Policy",
    sections: [
      {
        heading: "1. Purpose",
        content: [
          "S.A.N. Security Ltd is committed to ensuring that any use of force by its operatives is lawful, proportionate, necessary, and reasonable."
        ]
      },
      {
        heading: "2. Scope",
        content: [
          "This policy applies to all S.A.N. Security Ltd staff and contractors performing security duties."
        ]
      },
      {
        heading: "3. Legal Framework",
        content: [
          "Use of force must comply with the Criminal Law Act 1967, Common Law Self-Defence, Human Rights Act 1998, and SIA guidance."
        ]
      },
      {
        heading: "4. Principles of Use of Force",
        content: [
          "Force must be necessary, proportionate, reasonable, and justifiable."
        ]
      },
      {
        heading: "5. Approved Types of Force",
        content: [
          "Only approved techniques such as guiding, escorting, and trained restraint techniques may be used."
        ]
      },
      {
        heading: "6. De-escalation",
        content: [
          "Verbal communication and de-escalation must always be attempted where possible."
        ]
      },
      {
        heading: "7. Training",
        content: [
          "Only trained and licensed staff may use force."
        ]
      },
      {
        heading: "8. Medical Attention",
        content: [
          "Welfare checks and first aid must be provided following any use of force."
        ]
      },
      {
        heading: "9. Reporting",
        content: [
          "All use of force incidents must be reported using the official company form."
        ]
      },
      {
        heading: "10. Review and Investigation",
        content: [
          "All incidents will be reviewed and investigated where required."
        ]
      },
      {
        heading: "11. Misuse of Force",
        content: [
          "Misuse may result in disciplinary action or referral to authorities."
        ]
      },
      {
        heading: "12. Responsibility",
        content: [
          "Responsibility rests with S.A.N. Security Ltd management and individual staff."
        ]
      }
    ]
  },
  {
    id: 11,
    title: "Lost Property Policy",
    sections: [
      {
        heading: "1. Purpose",
        content: [
          "S.A.N. Security Ltd is committed to handling lost and found property responsibly, transparently, and in a manner that protects the rights of individuals, clients, and staff."
        ]
      },
      {
        heading: "2. Scope",
        content: [
          "This policy applies to all staff including security officers, door supervisors, stewards, cleaners, and dual-role staff across all sites and events."
        ]
      },
      {
        heading: "3. Definitions",
        content: [
          "Lost Property: An item reported as missing.",
          "Found Property: An item discovered by staff with no immediate identifiable owner."
        ]
      },
      {
        heading: "4. General Principles",
        content: [
          "All property must be recorded, secured, and never used for personal benefit."
        ]
      },
      {
        heading: "5. Lost Property Procedure",
        content: [
          "All reported losses must be recorded using the Lost Property Report Form and checked against existing records."
        ]
      },
      {
        heading: "6. Found Property Procedure",
        content: [
          "All found items must be recorded, secured, and stored in designated secure locations."
        ]
      },
      {
        heading: "7. High-Value Items",
        content: [
          "Cash, wallets, phones, ID documents, and valuables must receive extra care and immediate reporting."
        ]
      },
      {
        heading: "8. Release of Property",
        content: [
          "Items may only be released once ownership is verified and documented."
        ]
      },
      {
        heading: "9. Police Involvement",
        content: [
          "Police may be informed where required by law, suspicion, or client instruction."
        ]
      },
      {
        heading: "10. Staff Responsibilities",
        content: [
          "Staff must comply fully with this policy at all times."
        ]
      },
      {
        heading: "11. Management Responsibilities",
        content: [
          "Management will monitor compliance and handle escalations."
        ]
      },
      {
        heading: "12. Data Protection",
        content: [
          "All personal data will be processed in line with UK GDPR and company policy."
        ]
      },
      {
        heading: "13. Policy Review",
        content: [
          "This policy is reviewed periodically and updated as required."
        ]
      }
    ]
  },
  {
    id: 12,
    title: "Found Property Policy",
    sections: [
      {
        heading: "1. Purpose",
        content: [
          "This policy sets out S.A.N. Security Ltd. approach to the handling of property found by staff while on duty. The purpose is to ensure that found items are managed responsibly, lawfully, and with integrity, while protecting staff, the public, and the company."
        ]
      },
      {
        heading: "2. Scope",
        content: [
          "This policy applies to all staff engaged by S.A.N. Security Ltd across all deployments, including events, festivals, venues, and sites. It applies strictly to items found by staff while carrying out their duties and does not cover lost property claims made by individuals."
        ]
      },
      {
        heading: "3. General Principles",
        content: [
          "All property found by staff during duty is treated as potentially belonging to a member of the public or client. Found property must be handled honestly, transparently, and professionally. Staff must not retain, use, remove, or dispose of found property for personal use. All found property remains the responsibility of the company until handed over in line with operational arrangements."
        ]
      },
      {
        heading: "4. Staff Responsibilities",
        content: [
          "Staff who find property while on duty must act with integrity and honesty at all times, ensure the item is protected from loss or damage, follow company reporting requirements, and cooperate with supervisors or management where required. Failure to comply may be treated as a disciplinary matter."
        ]
      },
      {
        heading: "5. Prohibited Actions",
        content: [
          "Staff must not take found property for personal use, remove found property from site without authorisation, attempt to locate or contact the owner independently unless authorised, share information with unauthorised persons, or dispose of found property without instruction."
        ]
      },
      {
        heading: "6. Confidentiality",
        content: [
          "All information relating to found property is confidential and must only be shared with authorised persons within S.A.N. Security Ltd or as required by the client."
        ]
      },
      {
        heading: "7. Compliance",
        content: [
          "Compliance with this policy is mandatory. Breaches may result in disciplinary action, suspension, or termination of engagement."
        ]
      },
      {
        heading: "8. Review",
        content: [
          "This policy is reviewed periodically to ensure it remains appropriate, lawful, and aligned with operational requirements and best practice."
        ]
      }
    ]
  },
  {
    id: 13,
    title: "Payment Policy",
    sections: [
      {
        heading: "1. Purpose",
        content: [
          "This Payment Policy sets out how wages, expenses, and work-related payments are managed by S.A.N. Security Ltd. This policy applies to all staff, across all roles, deployments, events, venues, and sites."
        ]
      },
      {
        heading: "2. Pay Rates",
        content: [
          "Pay rates vary depending on the role, site, event type, and client requirements.",
          "All pay rates are at or above the UK National Minimum Wage / National Living Wage.",
          "Different roles may attract different pay rates.",
          "Pay rates are confidential and must not be discussed with other staff."
        ]
      },
      {
        heading: "3. Payroll Schedule",
        content: [
          "Wages are paid monthly, one month in arrears.",
          "Payroll is processed around the 30th of each month.",
          "Example: work completed in January is paid at the end of February."
        ]
      },
      {
        heading: "4. Confirmation of Hours",
        content: [
          "All staff must follow company procedures of sign in / sign out for every shift worked.",
          "Staff must log in and log out using an iPad or device provided on site.",
          "Staff are required to send a photo confirming arrival on site via WhatsApp to Admin.",
          "Only hours confirmed by the client and recorded correctly will be paid."
        ]
      },
      {
        heading: "5. Amendments to Hours",
        content: [
          "Any request to amend recorded hours must be submitted in writing at sansecuryty@gmail.com",
          "Amendments are subject to verification and client confirmation.",
          "The review process may take up to three (3) months.",
          "Approved additional hours will be back-paid once confirmed."
        ]
      },
      {
        heading: "6. Expenses and Travel",
        content: [
          "Staff are responsible for arranging and funding their own travel to and from work locations, unless otherwise confirmed in writing by S.A.N. Security Ltd.",
          "Approved expenses may be processed separately from payroll."
        ]
      },
      {
        heading: "7. Non-Payment Situations",
        content: [
          "Payment may be withheld or adjusted if a shift is not attended, procedures are not followed, or misconduct occurs."
        ]
      },
      {
        heading: "8. Payslips",
        content: [
          "Payslips are issued electronically.",
          "Payslip queries must be submitted in writing at sansecuryty@gmail.com"
        ]
      },
      {
        heading: "9. Payroll Queries",
        content: [
          "All payroll queries must be submitted in writing at sansecuryty@gmail.com"
        ]
      },
      {
        heading: "10. Compliance",
        content: [
          "This policy complies with UK employment law, HMRC payroll requirements, and minimum wage legislation."
        ]
      },
      {
        heading: "11. Acceptance",
        content: [
          "By accepting work with S.A.N. Security Ltd, staff confirm that they have read, understood, and agree to this Payment Policy."
        ]
      }
    ]
  }
];

// Collapsible Section Component
const CollapsibleSection = ({ title, children, defaultOpen = false, number }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-border rounded-sm overflow-hidden mb-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full flex items-center justify-between p-4 text-left transition-colors",
          isOpen ? "bg-accent/50" : "bg-card hover:bg-accent/30"
        )}
      >
        <div className="flex items-center gap-3">
          {number && (
            <span className="w-8 h-8 flex items-center justify-center bg-primary text-primary-foreground text-sm font-bold rounded-sm">
              {number}
            </span>
          )}
          <span className="font-barlow tracking-wider text-sm uppercase">{title}</span>
        </div>
        {isOpen ? (
          <ChevronDown className="w-5 h-5 text-primary" />
        ) : (
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 bg-muted/50 border-t border-border">
          {children}
        </div>
      )}
    </div>
  );
};

// Main Terms Content Component
export const TERMS_CATEGORIES_COUNT = termsCategories.length;

/**
 * Flatten the terms tree into a printable plain-text document. Used by the
 * Admin "Download PDF" button on the Terms & Conditions section so we have a
 * single source of truth for both the on-screen render and the PDF export.
 */
export function getTermsAsText() {
  const lines = [];
  termsCategories.forEach((cat, ci) => {
    lines.push('');
    lines.push(`${ci + 1}. ${(cat.title || '').toUpperCase()}`);
    if (cat.description) lines.push(cat.description);
    (cat.terms || []).forEach((t, ti) => {
      lines.push('');
      lines.push(`  ${ci + 1}.${ti + 1}  ${t.title || ''}`);
      (t.content || []).forEach((para) => lines.push(`    • ${para}`));
    });
  });
  return lines.join('\n');
}

export default function TermsContent() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('terms'); // 'terms' or 'policies'
  const [expandedCategories, setExpandedCategories] = useState({ 1: true }); // Event and Festival open by default

  const handlePrint = () => {
    window.print();
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Filter terms categories based on search
  const filteredCategories = termsCategories.map(category => ({
    ...category,
    terms: category.terms.filter(term =>
      term.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      term.content.some(c => c.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  })).filter(category => 
    category.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.terms.length > 0
  );

  // Count total terms
  const totalTermsCount = termsCategories.reduce((acc, cat) => acc + cat.terms.length, 0);

  const filteredPolicies = policiesData.filter(policy =>
    policy.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (policy.sections && policy.sections.some(section =>
      section.content.some(c => c.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (section.heading && section.heading.toLowerCase().includes(searchTerm.toLowerCase()))
    ))
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2 pb-6 border-b border-border">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Shield className="w-10 h-10 text-primary" />
        </div>
        <h1 className="text-2xl font-barlow tracking-wider uppercase text-primary">
          S.A.N. Security Ltd
        </h1>
        <h2 className="text-lg font-barlow tracking-wider uppercase">
          Company Internal Operational
        </h2>
        <h3 className="text-base font-barlow tracking-wider uppercase text-muted-foreground">
          Terms & Conditions and Company Policies
        </h3>
      </div>

      {/* Important Notice */}
      <div className="bg-red-900/20 border border-red-900/50 rounded-sm p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm space-y-2">
            <p className="text-red-400 font-medium">Important Notice</p>
            <p className="text-muted-foreground">
              These Terms and Conditions apply to all staff engaged by S.A.N. Security Ltd.
              Failure to comply may result in immediate removal from site, disciplinary action, 
              suspension from future bookings, or termination of engagement.
            </p>
            <p className="text-primary font-medium">
              Acceptance of a shift confirms agreement to these conditions.
            </p>
          </div>
        </div>
      </div>

      {/* Search and Actions */}
      <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search terms & policies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-card border-border rounded-sm"
          />
        </div>
        <Button
          onClick={handlePrint}
          variant="outline"
          className="border-border rounded-sm"
        >
          <Printer className="w-4 h-4 mr-2" />
          Print
        </Button>
      </div>

      {/* Tabs removed — Company Policies are now shown on the dedicated
          Legal & Policies page. This component only renders Terms now. */}
      <div className="flex border-b border-border">
        <div
          className="px-6 py-3 font-barlow tracking-wider text-sm uppercase text-primary border-b-2 border-primary"
        >
          <FileText className="w-4 h-4 inline-block mr-2" />
          Terms &amp; Conditions ({totalTermsCount})
        </div>
      </div>

      {/* Content */}
      {activeTab === 'terms' ? (
        <div className="space-y-4">
          {filteredCategories.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No terms found matching your search.</p>
          ) : (
            filteredCategories.map((category) => (
              <div key={category.id} className="border border-border rounded-sm overflow-hidden">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category.id)}
                  className={cn(
                    "w-full flex items-center justify-between p-4 text-left transition-colors",
                    expandedCategories[category.id] ? "bg-primary/10" : "bg-card hover:bg-accent/30"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center bg-primary text-primary-foreground text-lg font-bold rounded-sm">
                      {category.id}
                    </span>
                    <div>
                      <span className="font-barlow tracking-wider text-base uppercase block text-primary">
                        {category.title}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {category.terms.length > 0 ? `${category.terms.length} terms` : 'Coming soon'}
                      </span>
                    </div>
                    {category.icon === 'tent' && <Tent className="w-5 h-5 text-primary ml-2" />}
                    {category.icon === 'store' && <Store className="w-5 h-5 text-primary ml-2" />}
                  </div>
                  {expandedCategories[category.id] ? (
                    <ChevronDown className="w-6 h-6 text-primary" />
                  ) : (
                    <ChevronRight className="w-6 h-6 text-muted-foreground" />
                  )}
                </button>

                {/* Category Content - Nested Terms */}
                {expandedCategories[category.id] && (
                  <div className="p-4 bg-muted/30 border-t border-border">
                    {category.description && (
                      <p className="text-sm text-muted-foreground mb-4 italic">
                        {category.description}
                      </p>
                    )}
                    
                    {category.terms.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Store className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">Terms for this category will be added soon.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {category.terms.map((term) => (
                          <CollapsibleSection 
                            key={term.id} 
                            title={term.title} 
                            number={term.id}
                          >
                            <ul className="space-y-2">
                              {term.content.map((item, idx) => (
                                <li 
                                  key={idx} 
                                  className={cn(
                                    "text-sm",
                                    item === "" ? "h-2" : "",
                                    item.startsWith("•") ? "pl-4 text-muted-foreground" : "text-foreground"
                                  )}
                                >
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </CollapsibleSection>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      ) : null /* Policies tab removed — see Legal & Policies page */}

      {/* Footer */}
      <div className="border-t border-border pt-6 mt-8">
        <p className="text-center text-xs text-muted-foreground">
          S.A.N. Security Ltd - All rights reserved. Last updated: February 2026
        </p>
      </div>
    </div>
  );
}
